// Spec: .aidev/tasks/11083-preloader/docs/functional_requirements.md §Key decisions
//
// Mounts the bead field into the report's loading state. Owns everything that
// touches the page: which palette is active, whether motion is welcome, and the
// decision to hand back to the previous loader.

import PreloaderEngine from './engine'
import { mountSidebarShell, unmountSidebarShell } from '@aytm/app/stats/preloader/sidebar-shell'

// The report colour themes, in the order the Colors panel offers them.
const THEMES = [
  ['#8cc63f', '#60ac66', '#419982', '#1d83a2', '#1868a6', '#135182', '#003863'],
  ['#fcd921', '#fbb03b', '#f7931e', '#f15a24', '#ed1c24', '#c1272d', '#6e272d'],
  ['#c69c6d', '#a67c52', '#8c6239', '#754c24', '#603813', '#42210b', '#210f03'],
  ['#cccccc', '#b2b2b2', '#999999', '#808080', '#666666', '#4d4d4d', '#333333'],
  ['#cccccc', '#fbb03b', '#999999', '#8cc63f', '#666666', '#29abe2', '#333333'],
  ['#ed1c24', '#f7931e', '#fcd821', '#8cc63f', '#29abe2', '#0071bc', '#9e005d'],
  ['#ff0066', '#ff80b3', '#ffe6f0', '#e0e0e0', '#808080', '#404040', '#000000'],
  ['#f58000', '#ffc700', '#d75426', '#b73f7c', '#551b57', '#76c5e4', '#145a7b']
]

const HOST_ID = 'stats-preloader'

const hexRGB = h => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]

// The report's model is the one authority on colour: it resolves the chosen theme and any custom
// palette, and it knows both before the charts exist and after they are rebuilt. The class on the
// chart container is written once at render, so it is absent during the load and stale after a
// theme change -- reading it is what made the field blue whatever the reader had picked.
const BANDS = 7

export function reportPalette() {
  const model = window.StatPage && window.StatPage.model
  const colours = model && 'function' === typeof model.colorTheme ? model.colorTheme() : null
  const usable = Array.isArray(colours) ? colours.filter(c => 'string' === typeof c && /^#[0-9a-f]{6}$/i.test(c)) : []
  return usable.length ? usable.slice(0, BANDS).map(hexRGB) : THEMES[0].map(hexRGB)
}

function prefersReducedMotion() {
  return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
}

function canRender() {
  if (typeof window.Path2D !== 'function') return false
  const probe = document.createElement('canvas')
  return Boolean(probe.getContext && probe.getContext('2d'))
}

const LOADING_REGION = '#loading_message'
const ERROR_REGION = '#error_message'

const MESSAGE_SELECTOR = '#loading_message .panel-message'
const MESSAGE_PERIOD = 5000

// The report only populates this line once the server reports more than a
// minute of processing, so below that it stays blank. It is now the page's only
// "still working" signal, so the preloader keeps it fed from the first second.
const OWN_MESSAGES = [
  'Assembling your report, question by question.',
  'Weighing every answer against the rest of the sample.',
  'Taking soundings across every question.',
  'Reading the whole sample, so you can read the summary.',
  'Cross-tabulating. This is the patient part.',
  'Counting every respondent, checking every answer.'
]

let engine = null
let host = null
let messageTimer = null

// The report's own lines all explain the same ten-minute wait, so these say what
// the wait is for instead, and the two sets are interleaved rather than one
// replacing the other.
export function messagePool() {
  const own = window.page && window.page.lib && window.page.lib.loadingMessages
  const theirs = Array.isArray(own) ? own.map(stripMarkup).filter(t => t.trim().length > 1) : []
  if (!theirs.length) return OWN_MESSAGES.slice()

  // Interleaved, not appended: at five seconds a line, a set that waits its turn
  // at the back is a set nobody on a short report ever sees.
  const mixed = []
  for (let i = 0; i < Math.max(theirs.length, OWN_MESSAGES.length); i++) {
    if (i < OWN_MESSAGES.length) mixed.push(OWN_MESSAGES[i])
    if (i < theirs.length) mixed.push(theirs[i])
  }
  return mixed.filter((text, i) => mixed.indexOf(text) === i)
}

function stripMarkup(html) {
  const box = document.createElement('div')
  box.innerHTML = html
  return box.textContent || ''
}

// Rebuilt per character so each can reveal on its own delay.
function writeMessage(el, text) {
  el.textContent = ''
  el.classList.add('is-revealing')
  for (let i = 0; i < text.length; i++) {
    const span = document.createElement('span')
    span.textContent = text[i]
    span.style.animationDelay = `${(i * 0.016).toFixed(3)}s`
    el.appendChild(span)
  }
}

function startStatusLine() {
  const el = document.querySelector(MESSAGE_SELECTOR)
  if (!el || messageTimer) return
  const pool = messagePool()
  let i = 0
  writeMessage(el, pool[0])
  if (prefersReducedMotion()) return
  // Stepping in order, not at random: a random pick repeats often enough that
  // the reveal reads as nothing having happened.
  messageTimer = window.setInterval(() => {
    i = (i + 1) % pool.length
    writeMessage(el, pool[i])
  }, MESSAGE_PERIOD)
}

function stopStatusLine() {
  if (messageTimer) window.clearInterval(messageTimer)
  messageTimer = null
}

// The field simply stands down; it does not swap anything in. The previous dot
// loader stays parked in the markup as a manual revert, not as a runtime path --
// revealing it would start 52 infinite animations on the thread the report is
// loading on, which is the cost this replaced.
function standDown(reason) {
  if (host) host.setAttribute('hidden', 'hidden')
  if (engine) {
    engine.stop()
    engine = null
  }
  if (window.console && reason) window.console.info(`stats preloader: ${reason}`)
}

// The failure screen is a region of its own, with its own mark. Carrying the field across to it --
// rather than standing the field down and leaving that mark to answer for the failure -- is what
// puts the dead line in front of a reader whose report actually broke.
function placeHost(selector) {
  const region = document.querySelector(selector)
  if (!host || !region) return null
  if (host.parentElement !== region) {
    const panel = region.querySelector('.panel')
    if (panel) region.insertBefore(host, panel)
    else region.appendChild(host)
  }
  return region
}

// Only the region's own mark, never a `.loader` belonging to something nested inside it.
function showRegionMark(selector, on) {
  const mark = document.querySelector(`${selector} > .loader`)
  if (mark) mark.toggleAttribute('hidden', !on)
}

function applyErrorMode(on) {
  if (!engine) return
  engine.setError(on)
  if (on) {
    stopStatusLine()
    showRegionMark(ERROR_REGION, false)
    // the skeleton claims a report is still coming, which the failure screen has just denied
    unmountSidebarShell()
    return
  }
  showRegionMark(ERROR_REGION, true)
  startStatusLine()
}

export function mountPreloader(options = {}) {
  const { region: regionSelector = LOADING_REGION, error = false } = options || {}
  host = document.querySelector(`#${HOST_ID}`)
  if (!host) return null
  placeHost(regionSelector)

  // A running field moving between the two screens is a change of room, not a fresh start: the
  // beads it has already earned and the palette it has crossed to are both worth keeping.
  if (engine) {
    applyErrorMode(error)
    return engine
  }

  const canvas = host.querySelector('canvas')
  if (!canvas || !canRender()) {
    standDown('rendering surface unavailable')
    // the failure screen keeps its own mark when the field cannot draw, and has no line to feed
    if (!error) {
      startStatusLine()
      mountSidebarShell()
    }
    return null
  }

  try {
    engine = new PreloaderEngine(canvas, {
      palette: reportPalette(),
      seed: `aytm-${Math.floor(Math.random() * 1e6)}`,
      reducedMotion: prefersReducedMotion()
    })
  } catch {
    standDown('engine failed to start')
    if (!error) {
      startStatusLine()
      mountSidebarShell()
    }
    return null
  }

  host.removeAttribute('hidden')
  engine.start()
  if (error) {
    applyErrorMode(true)
    return engine
  }
  startStatusLine()
  mountSidebarShell()
  return engine
}

export function unmountPreloader() {
  stopStatusLine()
  if (engine) {
    engine.stop()
    engine = null
  }
  if (host) host.setAttribute('hidden', 'hidden')
  // hand the failure screen back the way it was found, mark included, and park the field where it
  // is authored -- otherwise the next failure inherits an empty room
  showRegionMark(ERROR_REGION, true)
  placeHost(LOADING_REGION)
  unmountSidebarShell()
}

/** The reader picked a different theme or palette; cross the field over to it. */
export function preloaderRepalette() {
  if (engine) engine.setPalette(reportPalette())
}

export default mountPreloader
